import { Component, OnInit, input } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../../../../forms/ziro-form-abstract.directive';
import { ZiroFormStateService } from '../../../../../../../forms/ziro-form-state.service';
import { Observable, of } from 'rxjs';
import { ZiroFormConfig } from '../../../../../../../forms/ziro-forms-models';
import {
  BandwidthEmergencyCallingSettingsConfig,
  TeamsPhoneNumberAssignment,
} from '../../../../../../../shared/models/generated/ziroModels';
import { ZiroSelectConfig } from '../../../../../../../forms/fields/select/ziro-select.component';

export interface BandwidthDynamicFormData {
  dynamicGeolocations: string;
  dynamicGeolocationCallerName: string;
  dynamicGeolocationCallbackNumber: string;
  dynamicGeolocationPreferredLanguage: string;
  dynamicGeolocationEndpointId: string;
}

@Component({
  selector: 'ziro-bandwidth-emergency-calling-form',
  templateUrl: './bandwidth-emergency-calling-form.component.html',
  standalone: false,
})
export class BandwidthEmergencyCallingFormComponent
  extends ZiroFormAbstractDirective<BandwidthDynamicFormData>
  implements OnInit
{
  protected _ziroFormStateService: ZiroFormStateService;

  readonly bandwidthFieldConfig = input<BandwidthEmergencyCallingSettingsConfig>();
  readonly bandwidthEntity = input<BandwidthDynamicFormData>();
  readonly teamsLine = input<TeamsPhoneNumberAssignment>();
  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    this._initFormConfig();
    if (this.teamsLine()) {
      this.entitySource.next(this.bandwidthEntity());
    } else {
      this.entitySource.next(this.getEntityFromFieldConfigDefaults());
    }
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private _initFormConfig(): void {
    const bandwidthFieldConfig = this.bandwidthFieldConfig();
    const bandwidthFieldConfigValue = this.bandwidthFieldConfig();
    this.formConfig = {
      fields: {
        dynamicGeolocations: {
          label: 'tkey;shared.model.microsoft_teams_calling.e911_location.label',
          componentConfig: new ZiroSelectConfig({
            options: bandwidthFieldConfig ? bandwidthFieldConfig.dynamicGeolocations.possibleOptions : [],
          }),
          dataAutomation: 'bandwidth-e911-location-information',
          required: () => true,
          hidden: () => !this.bandwidthFieldConfig().dynamicGeolocations.show,
          defaultValue: () => this.bandwidthFieldConfig().dynamicGeolocations.defaultOption,
          disabled: () => this.isSubmitting,
        },
        dynamicGeolocationCallerName: {
          label: 'tkey;shared.model.microsoft_teams_calling.e911_callerName.label',
          dataAutomation: 'bandwidth-e911-caller-name',
          required: () => true,
          hidden: () => !this.bandwidthFieldConfig().dynamicGeolocationCallerName.show,
          defaultValue: () => this.bandwidthFieldConfig().dynamicGeolocationCallerName.value,
          disabled: () => this.isSubmitting,
        },
        dynamicGeolocationCallbackNumber: {
          label: 'tkey;shared.model.microsoft_teams_calling.e911_callbackNumber.label',
          dataAutomation: 'bandwidth-e911-callback-number',
          required: () => true,
          hidden: () => !this.bandwidthFieldConfig().dynamicGeolocationCallbackNumber.show,
          defaultValue: () => this.bandwidthFieldConfig().dynamicGeolocationCallbackNumber.value,
          disabled: () => this.isSubmitting,
        },
        dynamicGeolocationPreferredLanguage: {
          label: 'tkey;shared.model.microsoft_teams_calling.e911_preferredLanguage.label',
          dataAutomation: 'bandwidth-e911-preferred-language',
          componentConfig: new ZiroSelectConfig({
            options: bandwidthFieldConfigValue
              ? bandwidthFieldConfigValue.dynamicGeolocationPreferredLanguage.possibleOptions
              : [],
          }),
          required: () => true,
          hidden: () => !this.bandwidthFieldConfig().dynamicGeolocationPreferredLanguage.show,
          defaultValue: () => this.bandwidthFieldConfig().dynamicGeolocationPreferredLanguage.defaultOption,
          disabled: () => this.isSubmitting,
        },
        dynamicGeolocationEndpointId: {
          label: 'tkey;shared.model.microsoft_teams_calling.e911_endpointId.label',
          dataAutomation: 'bandwidth-e911-endpoint-id',
          required: () => true,
          hidden: () => !this.bandwidthFieldConfig().dynamicGeolocationEndpointId.show,
          defaultValue: () => this.bandwidthFieldConfig().dynamicGeolocationEndpointId.value,
          disabled: () => this.isSubmitting,
        },
      },
    } as ZiroFormConfig;
  }

  private getEntityFromFieldConfigDefaults(): BandwidthDynamicFormData {
    return {
      dynamicGeolocations: this.bandwidthFieldConfig().dynamicGeolocations.defaultOption,
      dynamicGeolocationCallbackNumber: this.bandwidthFieldConfig().dynamicGeolocationCallbackNumber.value,
      dynamicGeolocationCallerName: this.bandwidthFieldConfig().dynamicGeolocationCallerName.value,
      dynamicGeolocationPreferredLanguage:
        this.bandwidthFieldConfig().dynamicGeolocationPreferredLanguage.defaultOption,
      dynamicGeolocationEndpointId: this.bandwidthFieldConfig().dynamicGeolocationEndpointId.value,
    };
  }
}
